import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CallAPI } from 'actions/General';
import { addTicketTypeSchema, priorityOptionsList } from './utils';
import FormButton from 'components/form-components/FormButton';
import TextField from 'components/form-components/TextField';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import ToggleSwitch from 'components/form-components/ToggleSwitch';
import NestedSelect from 'components/form-components/NestedSelect';
import MultiCheckBoxSelect from 'components/form-components/MultiCheckBoxSelect';
import gripVerticalIcon from 'assets/images/icons/grip-vertical.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import lockGreyIcon from 'assets/images/icons/lock-grey.svg';

const TicketTypeEditForm = ({ handleClose, refreshList, selectedRecordId, isEditing }) => {
  const { t } = useTranslation();

  const [values, setValues] = useState({
    name: '',
    companyField: false,
    companyFields: [],
    allowUserPrioritySelection: false,
    defaultPriority: null,
    publishTicketAfterSubmit: true,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [companyFieldOptions, setCompanyFieldOptions] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchCompanyFields = async () => {
    const result = await CallAPI('GET_ALL_COMPANY_FIELDS', {});
    if (result.status) {
      const options = result.data.map(({ _id, fieldTitle }) => ({
        value: _id,
        displayValue: fieldTitle,
      }));
      setCompanyFieldOptions(options);
    }
  };

  const fetchTicketTypeDetail = async () => {
    const result = await CallAPI('GET_TICKET_TYPE_DETAIL', null, selectedRecordId);
    if (result.status) {
      setSelectedRecord(result.data);
    } else toast.error(result.error);
  };

  useEffect(() => {
    if (isEditing) fetchTicketTypeDetail();
    fetchCompanyFields();
  }, []);

  const handleSubmit = async () => {
    try {
      setTouched({});
      const payload = {
        name: values.name.trim(),
        title: true,
        description: true,
        location: true,
        companyFields: values.companyField ? values.companyFields : [],
        allowUserPrioritySelection: values.allowUserPrioritySelection,
        defaultPriority: values.allowUserPrioritySelection ? null : values.defaultPriority,
        publishTicketAfterSubmit: values.publishTicketAfterSubmit,
      };
      await addTicketTypeSchema.validate(payload, { abortEarly: false });

      const apiName = isEditing ? 'UPDATE_TICKET_TYPE' : 'ADD_TICKET_TYPE';
      const recordId = isEditing ? selectedRecordId : null;

      const result = await CallAPI(apiName, payload, recordId, setLoading);
      if (result?.status) {
        toast.success(
          t(isEditing ? 'alert_success_ticket_type_updated' : 'alert_success_ticket_type_added'),
        );
        handleClose();
        refreshList();
      } else {
        toast.error(result?.error || t('alert_error_something_went_wrong'));
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message;
      });
      setErrors(schemaErrors);
    }
  };

  useEffect(() => {
    if (isEditing && selectedRecord) {
      setValues({
        name: selectedRecord.name,
        companyField: !!selectedRecord.companyFields?.length,
        companyFields: selectedRecord.companyFields,
        allowUserPrioritySelection: !selectedRecord.defaultPriority,
        defaultPriority: selectedRecord.defaultPriority,
        publishTicketAfterSubmit: selectedRecord.publishTicketAfterSubmit,
      });
    }
  }, [selectedRecord, isEditing]);

  const handleChange = (field, value) => {
    setValues({ ...values, [field]: value });
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
  };

  const getDisplayValueFromList = (list, value, defaultValue) => {
    return list.find((o) => o.value === value)?.displayValue ?? defaultValue;
  };

  const getMultiDisplayValuesFromList = (list, value, defaultValue) => {
    if (list.length === 0) return defaultValue;
    const displayValues = value.map((v) => {
      return list.find((l) => l.value === v).displayValue;
    });
    let result = '';
    const showMaxValues = 2;
    if (displayValues.length > showMaxValues) {
      result =
        displayValues.slice(0, 2).join(', ') + ` +${displayValues.length - showMaxValues} more`;
    } else {
      result = displayValues.join(', ');
    }
    return result || defaultValue;
  };

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='theme-font-inter-regular border-grey-13-h-1 rounded-sm'>
        <div className='d-flex flex-column theme-background-white-2 rounded-top p-2'>
          <span className='theme-size-1_3 theme-text-black-1'>{t('text_type_name')}</span>
          <span>{t('text_type_name_description')}</span>
        </div>
        <TextField
          name='name'
          classes='w-auto p-2'
          value={values.name}
          error={errors.name}
          touched={touched.name}
          handleChange={handleChange}
          placeholder='field_name'
          shrinkFieldOnly
        />
      </div>

      <div className='theme-font-inter-regular border-grey-13-h-1 rounded-sm'>
        <div className='d-flex flex-column theme-background-white-2 rounded-top p-2'>
          <span className='theme-size-1_3 theme-text-black-1'>{t('text_type_fields')}</span>
          <span>{t('text_type_fields_description')}</span>
        </div>
        <div className='d-flex justify-content-between p-2'>
          <div>
            <div className='d-flex gap-2'>
              <img src={gripVerticalIcon} />
              <span>{t('text_title_required')}</span>
            </div>
          </div>
          <img src={lockGreyIcon} />
        </div>
        <div className='d-flex justify-content-between p-2'>
          <div>
            <div className='d-flex gap-2'>
              <img src={gripVerticalIcon} />
              <span>{t('field_description')}</span>
            </div>
          </div>
          <img src={lockGreyIcon} />
        </div>
        <div className='d-flex justify-content-between p-2'>
          <div>
            <div className='d-flex gap-2'>
              <img src={gripVerticalIcon} />
              <span>{t('field_location')}</span>
            </div>
          </div>
          <img src={lockGreyIcon} />
        </div>
        <div className='d-flex flex-column p-2 gap-2'>
          <div className='d-flex justify-content-between'>
            <div>
              <div className='d-flex gap-2'>
                <img src={gripVerticalIcon} />
                <span>{t('field_company_field')}</span>
              </div>
            </div>
            <div className='d-flex align-items-center'>
              <ToggleSwitch
                name='companyField'
                value={values.companyField}
                handleChange={handleChange}
              />
            </div>
          </div>

          {values.companyField && (
            <div className='user-select-none'>
              <MultiCheckBoxSelect
                options={companyFieldOptions}
                value={values.companyFields}
                handleChange={handleChange}
                name='companyFields'
                toggleClasses='px-2 py-1 border-grey-1-h-1 align-items-center'
                toggleComponent={
                  <>
                    <label className='theme-size-0_9 cursor-pointer'>
                      {t(
                        getMultiDisplayValuesFromList(
                          companyFieldOptions,
                          values.companyFields,
                          'select_value',
                        ),
                      )}
                    </label>
                    <img src={chevronGreyIcon} className='rotate-180' />
                  </>
                }
                fullWidth
              />
            </div>
          )}
        </div>
      </div>

      <div className='theme-font-inter-regular border-grey-13-h-1 rounded-sm'>
        <div className='d-flex flex-column theme-background-white-2 rounded-top p-2'>
          <span className='theme-size-1_3 theme-text-black-1'>{t('text_priority')}</span>
          <span>{t('text_adjust_priority')}</span>
        </div>
        <div className='d-flex flex-column gap-2 p-2'>
          <div className='d-flex gap-3 align-items-center'>
            <span className='theme-size-1_1'>{t('text_let_user_select')}</span>
            <ToggleSwitch
              name='allowUserPrioritySelection'
              value={values.allowUserPrioritySelection}
              handleChange={handleChange}
            />
          </div>
          {values.allowUserPrioritySelection ? (
            <div className='d-flex flex-column gap-2'>
              {priorityOptionsList.map((o, i) => {
                return (
                  <div key={i} className='d-flex gap-2'>
                    <img src={o.icon} />
                    <span>{t(o.displayValue)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className='user-select-none'>
              <NestedSelect
                options={priorityOptionsList}
                name='defaultPriority'
                handleChange={handleChange}
                toggleComponent={
                  <>
                    <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                      {t(
                        getDisplayValueFromList(
                          priorityOptionsList,
                          values.defaultPriority,
                          'select_priority_for_this_type',
                        ),
                      )}
                    </label>
                    <img src={chevronGreyIcon} className='rotate-180' />
                  </>
                }
                toggleClasses='d-flex justify-content-between align-items-center px-2 py-1 border-grey-1-h-1 w-auto'
                fullWidth
                disableIconGrayscale
              />
              {errors.defaultPriority && (
                <p className='form-field-error'>{errors.defaultPriority}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className='theme-font-inter-regular border-grey-13-h-1 rounded-sm'>
        <div className='d-flex flex-column theme-background-white-2 rounded-top p-2'>
          <span className='theme-size-1_3 theme-text-black-1'>{t('text_workflow')}</span>
          <span>{t('text_ticket_type_workflow_description')}</span>
        </div>
        <div className='d-flex flex-column gap-2 p-2'>
          <div className='d-flex gap-3 align-items-center'>
            <span className='theme-size-1_1'>{t('text_publish_ticket_after_submit')}</span>
            <ToggleSwitch
              name='publishTicketAfterSubmit'
              value={values.publishTicketAfterSubmit}
              handleChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className='d-flex gap-2 justify-content-end p-3 border-top-grey-10-h-1'>
        <FormButton text={t('button_discard')} variant='white-1' onClick={handleClose} />
        <FormLoadingButton
          text={isEditing ? 'button_update' : 'button_add'}
          variant='green-1'
          loading={loading}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

TicketTypeEditForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
  selectedRecordId: PropTypes.string,
  isEditing: PropTypes.bool.isRequired,
};

export default TicketTypeEditForm;
